import { Link } from "react-router-dom";
import { Accessibility, CalendarClock, Check, LifeBuoy, ListChecks } from "lucide-react";
import { Notice, PageHero, SectionHead } from "../components/site/Blocks";
import { CtaBand } from "../components/site/Cta";
import { IndependentLivingQuestionnaire } from "../components/forms/IndependentLivingQuestionnaire";
import { AssistedLivingQuestionnaire } from "../components/forms/AssistedLivingQuestionnaire";

const INDEPENDENT = [
  "For adults who are able to live independently",
  "Shared accommodation, including shared living spaces and bedrooms",
  "Stable, comfortable housing in a respectful community setting",
];
const ASSISTED = [
  [LifeBuoy, "Day-to-day support", "Designed for adults who need help with meals, personal care, reminders and daily routines."],
  [Accessibility, "Accessibility", "Tell us about mobility, step-free access and other accessibility needs."],
  [CalendarClock, "First to hear", "People on the interest list are contacted first as opening details are confirmed."],
];

export default function Housing() {
  const scrollTo = (id) => document.getElementById(id)?.scrollIntoView({ behavior: "smooth", block: "start" });

  return (
    <>
      <PageHero eyebrow="Housing Programs" title="Independent living today. Assisted living, coming soon." image="/images/shared-bedroom-v2.jpg" alt="A clean shared bedroom with two twin beds" testId="housing-hero"
        actions={<div className="flex flex-wrap gap-3">
          <a href="#independent-living" className="btn-pill btn-accent" data-testid="housing-explore-cta-button">Explore Independent Living</a>
          <a href="#assisted-living" className="btn-pill btn-outline" data-testid="housing-assisted-cta-button">Assisted Living Interest List</a>
        </div>}>
        <p>Our housing programs give adults a stable place to live with dignity and independence. Independent living is available for adults who can live on their own, and our assisted living care home is coming soon for those who need more daily support.</p>
      </PageHero>

      <section id="independent-living" className="container-site py-16 sm:py-24 scroll-mt-24 grid lg:grid-cols-12 gap-10 items-center" data-testid="independent-living-section">
        <div className="lg:col-span-6">
          <SectionHead eyebrow="Independent Living" title="A stable home, on your own terms.">
            Our independent living program is for adults who can live on their own and are looking for safe, comfortable and appropriate accommodation. Every inquiry is reviewed individually, and we discuss your preferences, location and timing with you.
          </SectionHead>
          <button type="button" onClick={() => scrollTo("housing-questionnaire")} className="btn-pill btn-primary mt-8" data-testid="independent-inquiry-cta-button">Inquire about independent living</button>
        </div>
        <ul className="lg:col-span-6 grid gap-3">
          {INDEPENDENT.map((t) => (
            <li key={t} className="flex items-start gap-3 card-warm px-5 py-4 text-sm font-medium"><Check className="h-5 w-5 text-[#C86D51] shrink-0" aria-hidden="true" />{t}</li>
          ))}
        </ul>
      </section>

      <section id="assisted-living" className="bg-[#1E3A2B] text-[#FAF8F5] scroll-mt-24" data-testid="assisted-living-section">
        <div className="container-site py-16 sm:py-24 grid lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-7">
            <span className="inline-flex items-center gap-2 rounded-full bg-[#F5E6A3] text-[#1C2421] text-xs font-bold uppercase tracking-[0.18em] px-3 py-1 mb-4" data-testid="assisted-living-coming-soon-badge">Coming Soon</span>
            <span className="eyebrow !text-[#F5E6A3] block">Assisted Living Care Home</span>
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-semibold tracking-tight">Join the interest list for our assisted living care home.</h2>
            <div className="mt-6 space-y-4 text-[#E5E0D8] leading-relaxed">
              <p>Our assisted living care home is coming soon. It is a senior care home for older adults who need help with bathing, meals, medication and daily routines in a warm, home-like setting.</p>
              <p>Join the interest list by sharing a few details about the person who may need care. We will keep you informed and contact you first as location, opening and admission details are confirmed. Joining the list does not reserve a room.</p>
            </div>
            <button type="button" onClick={() => scrollTo("assisted-living-questionnaire")} className="btn-pill btn-accent mt-8" data-testid="residential-inquiry-cta-button">
              <ListChecks className="h-4 w-4" /> Join the interest list
            </button>
          </div>
          <div className="lg:col-span-5 grid gap-4">
            {ASSISTED.map(([Icon, t, d]) => (
              <div key={t} className="flex gap-4 rounded-2xl border border-white/15 bg-white/5 p-5">
                <Icon className="h-5 w-5 text-[#F5E6A3] shrink-0 mt-0.5" aria-hidden="true" />
                <div><p className="font-semibold">{t}</p><p className="text-sm text-[#E5E0D8] mt-1">{d}</p></div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section id="housing-questionnaire" className="container-site py-16 sm:py-24 scroll-mt-24 grid lg:grid-cols-12 gap-10">
        <div className="lg:col-span-4 space-y-6">
          <SectionHead eyebrow="Independent living inquiry" title="Apply for independent living.">
            Our independent living program is for single adults who are comfortable sharing a home. Answer a few questions and we will contact you about next steps.
          </SectionHead>
          <Notice testId="housing-availability-notice">Submitting an inquiry does not reserve a bed or guarantee a placement.</Notice>
        </div>
        <div className="lg:col-span-8"><IndependentLivingQuestionnaire /></div>
      </section>

      <section id="assisted-living-questionnaire" className="bg-[#F3EFEA] border-y border-[#E5E0D8] scroll-mt-24">
        <div className="container-site py-16 sm:py-24 grid lg:grid-cols-12 gap-10">
          <div className="lg:col-span-4 space-y-6">
            <SectionHead eyebrow="Assisted living interest list" title="Join the interest list for our senior care home.">
              Tell us about the senior who may need care and the support they need day to day.
            </SectionHead>
            <Notice testId="assisted-availability-notice">Our assisted living care home is coming soon. Joining the interest list does not reserve a room or guarantee admission.</Notice>
          </div>
          <div className="lg:col-span-8"><AssistedLivingQuestionnaire /></div>
        </div>
      </section>
      <CtaBand prefix="housing-band" title="Own or manage residential property?" text="Rent your home or rental property to us and we will run it as a housing program for the people we serve.">
        <Link to="/partner-with-us#partner-inquiry" className="btn-pill btn-primary" data-testid="housing-band-partner-cta-button">Partner With Us</Link>
      </CtaBand>
    </>
  );
}
